import React, { useCallback, useMemo, useState } from 'react';

import { TotpChallengeStep, TotpEnrolmentStep } from './twoFactorSteps';
import { createTwoFactorCancelledError } from '../../helpers/twoFactorAuthentication';

const TWO_FACTOR_CHALLENGE = 'challenge';
const TWO_FACTOR_ENROLMENT = 'enrolment';

let twoFactorAttempt = 0;

/**
 * Bridges the promise based 2FA helpers and the 2FA screens: the request functions
 * show a step and resolve with what the user entered.
 */
export default function useTwoFactorPrompt() {
  // Pending 2FA input: { type, details, resolve, reject, attempt }
  const [twoFactorStep, setTwoFactorStep] = useState(null);

  const requestInput = useCallback(
    (type) => (details) =>
      new Promise((resolve, reject) => {
        twoFactorAttempt += 1;
        setTwoFactorStep({
          type,
          details,
          resolve,
          reject,
          attempt: twoFactorAttempt,
        });
      }),
    [],
  );

  const submit = (value) => {
    twoFactorStep.resolve(value);
    setTwoFactorStep(null);
  };

  const cancel = () => {
    twoFactorStep.reject(createTwoFactorCancelledError());
    setTwoFactorStep(null);
  };

  /**
   * @param {object} options
   * @param {string} [options.skipText] label of the skip button on the enrolment step
   */
  const renderTwoFactorStep = ({ skipText, wide } = {}) => {
    const { type, details, attempt } = twoFactorStep;
    if (type === TWO_FACTOR_CHALLENGE) {
      return (
        <TotpChallengeStep
          key={attempt}
          invalidCode={details.invalidCode}
          onSubmit={submit}
          onCancel={cancel}
        />
      );
    }
    return (
      <TotpEnrolmentStep
        key={attempt}
        {...details}
        skipText={skipText}
        wide={wide}
        onSubmit={submit}
        onSkip={() => submit(null)}
        onCancel={cancel}
      />
    );
  };

  const requestSignInCode = useMemo(
    () => requestInput(TWO_FACTOR_CHALLENGE),
    [requestInput],
  );
  const requestEnrolmentCode = useMemo(
    () => requestInput(TWO_FACTOR_ENROLMENT),
    [requestInput],
  );
  const clearTwoFactorStep = useCallback(() => setTwoFactorStep(null), []);

  return {
    twoFactorStep,
    requestSignInCode,
    requestEnrolmentCode,
    renderTwoFactorStep,
    clearTwoFactorStep,
  };
}
